import { Box, Drawer, Typography, IconButton, FormHelperText } from '@mui/material';
import Grid from '@mui/material/Grid2';
import Input from 'components/Common/Input';
import { useForm, Controller } from 'react-hook-form';
import Button from 'components/Common/Button';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'components/Common/Select';
import { useCreateOrUpdateTeamMember, useGetTeamMember } from 'apis/business/setting/team/hook';
import { zodResolver } from '@hookform/resolvers/zod';
import { teamMemberSchema } from 'utils/schema/validator/TeamMemberValidation';
import TelInput from 'components/Common/TelInput';
import { useGetLocations } from '@/apis/business/setting/location/hook';
import { IoClose } from 'react-icons/io5';
import { Checkbox, FormControlLabel } from '@mui/material';
import { TeamMemberContentSkeleton, TeamMemberFooterSkeleton } from './TeamMemberFormSkeleton';

export default function TeamForm() {
  const { teamId } = useParams();
  const navigate = useNavigate();

  const { data: teamMember, isLoading } = useGetTeamMember({ include: 'locations' });
  const { data: LocationsData } = useGetLocations({ per_page: 9999 });
  const defaultLocationIds = teamMember?.locations?.map((loc: any) => loc.id) || [];

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(teamMemberSchema),
    mode: 'onBlur',
    values: {
      team_member: {
        ...teamMember,
        location_ids: defaultLocationIds
      }
    }
  });

  const { mutate: handleTeamMember, isPending } = useCreateOrUpdateTeamMember();
  const isLocationAdmin = watch('team_member.role') === 'location_admin';

  const onSubmit = (data: any) => {
    if (data.team_member.role === 'business_admin') delete data.team_member.location_ids;
    handleTeamMember(data);
  };

  return (
    <Drawer
      anchor='right'
      open={true}
      onClose={() => navigate(-1)}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 400, md: 600 },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 100001
        }
      }}
    >
      {/* Fixed Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: '1px solid #DEDEDE',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}
      >
        <Typography variant='h6'>{teamId ? 'Edit' : 'Add'} Member</Typography>
        <IconButton onClick={() => navigate(-1)} size='small'>
          <IoClose size={24} />
        </IconButton>
      </Box>

      {/* Form with Fixed Footer */}
      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Scrollable Content */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 3
          }}
        >
          {isLoading ? (
            <TeamMemberContentSkeleton />
          ) : (
            <Grid container spacing='26px'>
              <Grid size={12}>
                <Input name='team_member.name' label='Name' control={control} fullWidth required />
              </Grid>
              <Grid size={12}>
                <Input name='team_member.email' label='Email' control={control} fullWidth required />
              </Grid>
              <Grid size={12}>
                <TelInput
                  name='team_member.phone_number'
                  label='Phone'
                  control={control}
                  defaultCountry='US'
                  fullWidth
                />
              </Grid>
              <Grid size={12}>
                <Select
                  name='team_member.role'
                  label='Role'
                  control={control}
                  fullWidth
                  options={[
                    { label: 'Business Admin', value: 'business_admin' },
                    { label: 'Location Admin', value: 'location_admin' }
                  ]}
                  required
                />
              </Grid>

              {/* Location checkboxes */}
              {isLocationAdmin && (
                <Grid size={12}>
                  <Typography fontSize={16} fontWeight={500}>
                    Locations
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                    <Controller
                      name='team_member.location_ids'
                      control={control}
                      render={({ field }) => {
                        return (
                          <>
                            {LocationsData?.data.map((location) => {
                              const isChecked = field.value?.includes(location.id) || false;
                              return (
                                <FormControlLabel
                                  key={location.id}
                                  control={
                                    <Checkbox
                                      checked={isChecked}
                                      onChange={(e) => {
                                        let newValue = [...(field.value || [])];
                                        if (e.target.checked) {
                                          newValue.push(location.id);
                                        } else {
                                          newValue = newValue.filter((id) => id !== location.id);
                                        }
                                        field.onChange(newValue);
                                      }}
                                    />
                                  }
                                  label={location.name}
                                />
                              );
                            })}
                            {/* Display error message */}
                            {errors?.team_member?.location_ids && (
                              <FormHelperText error>{errors.team_member.location_ids.message}</FormHelperText>
                            )}
                          </>
                        );
                      }}
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          )}
        </Box>

        {/* Fixed Footer */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid #DEDEDE',
            display: 'flex',
            justifyContent: 'end',
            gap: '16px',
            flexShrink: 0
          }}
        >
          {isLoading ? (
            <TeamMemberFooterSkeleton />
          ) : (
            <>
              {' '}
              <Button
                color='secondary'
                variant='outlined'
                sx={{ border: '1px solid #F3F3F3' }}
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
              <Button type='submit' variant='contained' isPending={isPending}>
                {teamId ? 'Save' : 'Add'}
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
